import React, { useState, useEffect } from 'react';
import { AlertCircle, Activity } from 'lucide-react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import ActivityTable from './components/ActivityTable';
import FilterControls from './components/FilterControls';
import ActivityDetailModal from './components/ActivityDetailModal';
import ExportControls from './components/ExportControls';
import userService from '../../services/userService';
import realtimeService from '../../services/realtimeService';
import { useAuth } from '../../contexts/AuthContext';

export default function UserActivityLogs() {
  const { userProfile } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    role: 'all',
    actionType: 'all',
    dateRange: 'all',
    search: ''
  });
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [newActivityCount, setNewActivityCount] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Extract artist brand colors — only applied when the logged-in user is an artist
  const isArtist = userProfile?.role === 'artist';
  const highlightColor = isArtist
    ? (userProfile?.highlight_color || userProfile?.highlightColor || null)
    : null;
  const secondaryColor = isArtist
    ? (userProfile?.secondary_color || userProfile?.secondaryColor || highlightColor || null)
    : null;

  // Load initial activity logs
  useEffect(() => {
    loadActivityLogs();
  }, []);

  // Real-time subscription for new activities
  useEffect(() => {
    let unsubscribe;

    const setupRealtime = async () => {
      try {
        unsubscribe = realtimeService?.subscribeToUserActivity((newActivity) => {
          // Add new activity to the top of the list
          setActivities(prev => [newActivity, ...prev]);
          setNewActivityCount(prev => prev + 1);
          setRealtimeConnected(true);

          // Reset counter after 3 seconds
          setTimeout(() => {
            setNewActivityCount(0);
          }, 3000);
        });
      } catch (err) {
        console.error('Real-time subscription error:', err);
        setRealtimeConnected(false);
      }
    };

    setupRealtime();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const loadActivityLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userService?.getAllUserActivity();
      setActivities(data || []);
    } catch (err) {
      setError(err?.message);
      console.error('Error loading activity logs:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle view details
  const handleViewDetails = (activity) => {
    setSelectedActivity(activity);
    setIsDetailModalOpen(true);
  };

  // Handle export
  const handleExport = async (format) => {
    try {
      if (format === 'csv') {
        await userService?.exportActivityLogs(filters);
      }
    } catch (err) {
      console.error('Error exporting activity logs:', err);
      setError('Failed to export activity logs');
    }
  };

  // Filtered activities based on current filters
  const filteredActivities = activities?.filter(activity => {
    const matchesRole = filters?.role === 'all' || activity?.role === filters?.role;
    const matchesActionType = filters?.actionType === 'all' || activity?.action_type === filters?.actionType;
    const matchesDateRange = filters?.dateRange === 'all' || 
      (filters?.dateRange === 'today' && new Date(activity.created_at)?.toDateString() === new Date()?.toDateString());
    const matchesSearch = !filters?.search || 
      activity?.user_id?.includes(filters?.search) || 
      activity?.action_type?.includes(filters?.search) ||
      activity?.description?.includes(filters?.search);
    
    return matchesRole && matchesActionType && matchesDateRange && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Artist brand color header accent strip */}
      {highlightColor && (
        <div
          className="h-1 w-full"
          style={{ background: `linear-gradient(to right, ${highlightColor}, ${secondaryColor || highlightColor}, transparent)` }}
        />
      )}

      <div className="flex">
        <Sidebar onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className="flex-1 p-8 ml-64">
          <div className="max-w-7xl mx-auto">
            {/* Real-time Status Indicator */}
            <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${realtimeConnected ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
                  <div className="flex items-center space-x-2">
                    <Activity size={18} className={realtimeConnected ? 'text-green-600' : 'text-gray-400'} />
                    <span className="text-sm font-medium text-gray-700">
                      {realtimeConnected ? 'Live Activity Monitoring Active' : 'Real-time Monitoring Disconnected'}
                    </span>
                  </div>
                </div>
                {newActivityCount > 0 && (
                  <div className="flex items-center space-x-2 bg-blue-50 px-3 py-1 rounded-full animate-pulse">
                    <span className="text-sm font-medium text-blue-700">
                      {newActivityCount} new activit{newActivityCount !== 1 ? 'ies' : 'y'}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-1">
                {highlightColor && (
                  <div
                    className="w-1 h-8 rounded-full"
                    style={{ background: `linear-gradient(to bottom, ${highlightColor}, ${secondaryColor || highlightColor})` }}
                  />
                )}
                <h1 className="text-3xl font-bold text-gray-900">User Activity Logs</h1>
              </div>
              <p className="text-gray-600 mt-2">
                Monitor all user actions and system events with real-time updates
              </p>
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <p className="text-red-800 font-medium">Error Loading Activity Logs</p>
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              </div>
            )}

            {/* Filter Controls */}
            <FilterControls
              filters={filters}
              onFilterChange={(filterName, value) => {
                setFilters(prev => ({ ...prev, [filterName]: value }));
              }}
              onClearFilters={() => {
                setFilters({
                  role: 'all',
                  actionType: 'all',
                  dateRange: 'all',
                  search: ''
                });
              }}
            />

            {/* Export Controls */}
            <ExportControls onExport={handleExport} disabled={loading} />

            {/* Activity Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div
                className="bg-white p-4 rounded-lg shadow-sm border"
                style={highlightColor ? { borderColor: `${highlightColor}30` } : { borderColor: '#E5E7EB' }}
              >
                <p className="text-sm text-gray-600">Total Activities</p>
                <p
                  className="text-2xl font-bold"
                  style={highlightColor ? { color: highlightColor } : { color: '#111827' }}
                >{activities?.length}</p>
              </div>
              <div
                className="bg-white p-4 rounded-lg shadow-sm border"
                style={highlightColor ? { borderColor: `${highlightColor}30` } : { borderColor: '#E5E7EB' }}
              >
                <p className="text-sm text-gray-600">Today</p>
                <p
                  className="text-2xl font-bold"
                  style={highlightColor ? { color: highlightColor } : { color: '#111827' }}
                >
                  {activities?.filter(a => {
                    const today = new Date()?.setHours(0,0,0,0);
                    return new Date(a.created_at)?.setHours(0,0,0,0) === today;
                  })?.length}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <p className="text-sm text-gray-600">Unique Users</p>
                <p className="text-2xl font-bold text-gray-900">
                  {new Set(activities.map(a => a.user_id))?.size}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                <p className="text-sm text-gray-600">Live Updates</p>
                <p className="text-2xl font-bold text-green-600">
                  {realtimeConnected ? 'Active' : 'Inactive'}
                </p>
              </div>
            </div>

            {/* Activity Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <ActivityTable
                activities={filteredActivities}
                loading={loading}
                onViewDetails={handleViewDetails}
                currentPage={1}
                totalPages={1}
                pageSize={50}
                totalCount={activities?.length}
                onPageChange={() => {}}
                onPageSizeChange={() => {}}
              />
            </div>
          </div>
        </main>
      </div>
      {/* Activity Detail Modal */}
      {isDetailModalOpen && selectedActivity && (
        <ActivityDetailModal
          activity={selectedActivity}
          onClose={() => {
            setIsDetailModalOpen(false);
            setSelectedActivity(null);
          }}
        />
      )}
    </div>
  );
}